import { DEFAULT_MAP_IMAGES } from 'hooks/map/usePreloadDefaultImages';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { EToastType } from 'libs/types/site/IClient';
import { getCoordinatesBoundaries, parseStravaGPX } from 'libs/utils/gpx/gpxUtils';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import polylabel from 'polylabel';
import { useContext } from 'react';
import {
    ICoordinates,
    IDirectionWorldMarker,
    IIconV2World,
    IIconV2WorldMarker,
    IImageV2WorldMarker,
    IIndoorStandWorld,
    IIndoorStandWorldMarker,
    ILineV2WorldMarker,
    IPolygonV2WorldMarker,
    IRouteV2WorldMarker,
    ITextV2WorldMarker,
    IVariantIconV2World,
    IVariantIconV2WorldMarker,
    IWallV2WorldMarker
} from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { useStyles } from 'static/config/stylesConfig';
import { getTransportPath } from 'static/externalApi/directionApi';
import { Localization } from 'static/localization/localization';

export const V2_WALL_LINE_DEFAULT_WIDTH = 0.2;
export const EMPTY_IMAGE_URL =
    'https://dekba9e71gqjn.cloudfront.net/3d0def48-5dc9-4e1e-8033-976489e688bf.png';

export const useCreateWorldMarkerV2 = () => {
    const worldMarker = useWorldMarker();
    const ctxEdit = useContext(CtxEdit);

    const currentMapStyle = ctxEdit.map.defaultStyles[0];
    const styles = useStyles();
    const isDarkMap = styles.isDarkMapStyle(currentMapStyle);

    function generateNewBaseMarker(layerTitle?: string) {
        const id = Math.random().toString();
        const title = layerTitle
            ? {
                  layerTitle
              }
            : {};
        return {
            id: id,
            selectable: true,
            visible: true,
            scalable: true,
            scale: 1,
            isExternalPoint: false,
            order: worldMarker.findHighestOrder() + 1,
            ...title
        };
    }

    function createLine(lat: number, lng: number) {
        const marker: ILineV2WorldMarker = {
            ...generateNewBaseMarker(),
            lat,
            lng,
            elementType: 'v2/line' as const,
            elementData: {
                color: ctxEdit.markerDefault.color,
                width: 3,
                opacity: 1,
                dashed: {
                    isDashed: false,
                    lineLength: 5,
                    gapLength: 3
                },
                rawPaintAttributes: {},
                rawLayoutAttributes: {},
                coordinates: [
                    {
                        lat,
                        lng
                    },
                    {
                        lat: lat + 0.000000001,
                        lng: lng + 0.000000001
                    }
                ]
            }
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: false
        });
        return marker;
    }

    function createPolygon(lat: number, lng: number) {
        const marker: IPolygonV2WorldMarker = {
            ...generateNewBaseMarker(),
            lat,
            lng,
            elementType: 'v2/polygon' as const,
            elementData: {
                coordinates: [
                    { lng, lat: lat },
                    { lng: lng + 0.000000001, lat: lat + 0.000000001 }
                ],
                fill: {
                    isFilled: true,
                    color: ctxEdit.markerDefault.color,
                    opacity: 0.5,
                    rawPaintAttributes: {},
                    rawLayoutAttributes: {}
                },
                border: {
                    hasBorder: true,
                    color: ctxEdit.markerDefault.color,
                    width: 3,
                    opacity: 1,
                    rawPaintAttributes: {},
                    rawLayoutAttributes: {}
                }
            }
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: false
        });
        return marker;
    }

    function createText(lat: number, lng: number) {
        const center = ctxEdit.mapRef?.current.project([lng, lat]);
        const corners = [
            { x: center.x - 105, y: center.y - 25 },
            { x: center.x + 105, y: center.y - 25 },
            { x: center.x + 105, y: center.y + 25 },
            { x: center.x - 105, y: center.y + 25 }
        ];
        const coordinates = corners
            .map((corner) => ctxEdit.mapRef?.current.unproject([corner.x, corner.y]))
            .map((e) => ({ lat: e.lat, lng: e.lng }));

        const marker: ITextV2WorldMarker = {
            ...generateNewBaseMarker(),
            lat,
            lng,
            elementType: 'v2/text' as const,
            elementData: {
                imageUrl: EMPTY_IMAGE_URL,
                coordinates: coordinates,
                nonRotatedCoordinates: coordinates,
                opacity: 1,
                rotation: 0,
                generatedText: 'Text',
                generatedTextColor: '#ffffff',
                generatedBackgroundColor: ctxEdit.markerDefault.color,
                generatedWidth: 100,
                lineLength: 1,
                rawPaintAttributes: {},
                rawLayoutAttributes: {}
            }
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: true
        });
        return marker;
    }

    function createVariantIcon(lat: number, lng: number) {
        const id = Math.random().toString();

        const data: IVariantIconV2World = {
            text: 'Marker',
            imageUrl: null,
            color: '#000',
            storyImageUrl: '',
            circleText: null,
            withStar: false,
            useStoryImage: false,
            eventDate: null
        };

        const marker: IVariantIconV2WorldMarker = {
            ...generateNewBaseMarker(),
            id,
            lat,
            lng,
            elementType: 'v2/variant_icon' as const,
            elementData: data
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: true
        });
        return marker;
    }

    function createIcon(
        lat: number,
        lng: number,
        elementData?: Partial<IIconV2WorldMarker['elementData']>,
        layerTitle?: string
    ) {
        const id = Math.random().toString();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: IIconV2World = {
            imageUrl: `temp-icon-${id}`,
            text: 'Marker',
            imageSize: ctxEdit.markerDefault.pin.imageSize,
            textSize: ctxEdit.markerDefault.pin.textSize,
            textHaloColor: isDarkMap ? '#482b2b' : '#fff',
            textHaloWidth: 1,
            generatedBackgroundColor: ctxEdit.markerDefault.pin.generatedBackgroundColor,
            generatedEmoji: null,
            generatedIconColor: ctxEdit.markerDefault.pin.generatedIconColor,
            generatedIconUrl: ctxEdit.markerDefault.pin.generatedIconUrl,
            generatedImageUrl: null,
            textColor: isDarkMap ? '#fff' : '#000',
            rawPaintAttributes: {},
            rawLayoutAttributes: {
                'icon-anchor': 'bottom'
            }
        };

        data = {
            ...data,
            ...elementData
        };

        const marker: IIconV2WorldMarker = {
            ...generateNewBaseMarker(layerTitle),
            id,
            lat,
            lng,
            elementType: 'v2/icon' as const,
            elementData: data
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: true
        });
        return marker;
    }

    function createIndoorStand(lat: number, lng: number) {
        const id = Math.random().toString();

        const data: IIndoorStandWorld = {
            text: 'Stand',
            pinGeneratedType: 'icon',
            pinGeneratedImageUrl: DEFAULT_MAP_IMAGES.indoorStandGreen,
            pinGeneratedIconUrl:
                'https://dvtqxks0zalev.cloudfront.net/custom-icons/location-dot.svg',
            pinGeneratedIconColor: ctxEdit.markerDefault.color,
            logoGeneratedImageUrl: '',
            logoGeneratedStyleId: 'white-box',
            pinSrc: `temp-icon-${id}`,
            imageSrc: `temp-icon|-${id}`,
            textColor: '#000',
            textHaloColor: '#fff',
            textHaloWidth: 1
        };

        const marker: IIndoorStandWorldMarker = {
            ...generateNewBaseMarker(),
            id,
            lat,
            lng,
            elementType: 'indoor_stand' as const,
            elementData: data
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: true
        });
        return marker;
    }

    function createImage(
        source: { type: 'icon' | 'image'; url: string },
        coordinates: ICoordinates[]
    ) {
        const [centerLat, centerLng] = polylabel([coordinates.map(({ lat, lng }) => [lat, lng])]);
        const marker: IImageV2WorldMarker = {
            ...generateNewBaseMarker(),
            lat: centerLat,
            lng: centerLng,
            elementType: 'v2/image' as const,
            elementData: {
                // We use empty image to initially create the symbol
                imageUrl: source.type === 'image' ? source.url : EMPTY_IMAGE_URL,
                coordinates,
                nonRotatedCoordinates: coordinates,
                rotation: 0,
                opacity: 1,
                generatedBackgroundColor: null,
                generatedImageUrl: source.type === 'image' ? source.url : null,
                generatedEmoji: null,
                generatedIconColor: ctxEdit.markerDefault.color,
                generatedIconUrl: source.type === 'icon' ? source.url : null,
                flipHorizontal: false,
                flipVertical: false
            }
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: true
        });
        return marker;
    }

    function createWall(lat: number, lng: number, type: 'line' | 'polygon') {
        const coordinates = [
            { lng, lat: lat },
            { lng: lng + 0.000000001, lat: lat + 0.000000001 }
        ];
        const marker: IWallV2WorldMarker = {
            ...generateNewBaseMarker(),
            lat,
            lng,
            elementType: 'v2/wall' as const,
            elementData: {
                label: '',
                nonTransformedCoordinates: coordinates,
                rotation: 0,
                scale: 1,
                coordinates: [
                    { lng, lat: lat },
                    { lng: lng + 0.000000001, lat: lat + 0.000000001 }
                ],
                wall: {
                    height: 0,
                    color: '#555',
                    opacity: 1,
                    baseHeight: 0
                },
                line: {
                    isLine: type === 'line',
                    hasFloor: false,
                    width: V2_WALL_LINE_DEFAULT_WIDTH,
                    fillColor: '#fff'
                }
            }
        };
        ctxEdit.markerCreation.initiate({
            marker,
            isDone: false
        });
        return marker;
    }

    function createDirection(coordinate: ICoordinates) {
        const { lat, lng } = coordinate;
        const marker: IDirectionWorldMarker = {
            ...generateNewBaseMarker(),
            lat,
            lng,
            elementType: 'direction' as const,
            elementData: {
                coordinates: [
                    { lng, lat: lat },
                    { lng: lng + 0.000000001, lat: lat + 0.000000001 }
                ],
                path: [
                    { lng, lat: lat },
                    { lng: lng + 0.000000001, lat: lat + 0.000000001 }
                ],
                transport: 'walking',
                color: ctxEdit.markerDefault.color,
                opacity: 1,
                width: 3
            },
            scalable: false,
            order: worldMarker.findHighestOrder() + 1
        };

        ctxEdit.markerCreation.initiate({
            marker,
            isDone: false
        });
        return marker;
    }

    async function finishDirection(newCoordinates: ICoordinates) {
        const marker = ctxEdit.markerCreation.marker as IDirectionWorldMarker;
        const coordinates = [marker.elementData.coordinates[0], newCoordinates];
        const { path, waypoints } = await getTransportPath(
            marker.elementData.transport,
            coordinates
        );
        const finishedMarker = {
            ...marker,
            elementData: { ...marker.elementData, coordinates: waypoints, path }
        };

        ctxEdit.markerCreation.finish(finishedMarker);
    }

    function createRoute(url: string): Promise<IRouteV2WorldMarker> {
        const request = new XMLHttpRequest();

        return new Promise((resolve) => {
            request.addEventListener(
                'load',
                function (evt) {
                    const content = evt?.target?.['response'];
                    if (content) {
                        const coordinates = parseStravaGPX(content);
                        if (!coordinates || coordinates.length === 0) {
                            userMessage(EToastType.ERROR, Localization.UNKNOWN_GPX_FILE);
                            return;
                        }
                        const { center } = getCoordinatesBoundaries(coordinates);
                        const marker: IRouteV2WorldMarker = {
                            ...generateNewBaseMarker(),
                            lat: center.lat,
                            lng: center.lng,
                            elementType: 'v2/route' as const,
                            elementData: {
                                coordinates,
                                color: ctxEdit.markerDefault.color,
                                opacity: 1,
                                width: 3
                            },
                            scalable: false,
                            order: worldMarker.findHighestOrder() + 1
                        };

                        ctxEdit.markerCreation.initiate({
                            marker,
                            isDone: true
                        });

                        resolve(marker);
                    }
                },
                false
            );

            request.open('GET', url, true), request.send();
            ctxEdit.setWorldAction(EWorldAction.SELECT);
        });
    }

    return {
        createLine,
        createPolygon,
        createIcon,
        createVariantIcon,
        createText,
        createWall,
        createImage,
        createDirection,
        finishDirection,
        createRoute,
        createIndoorStand
    };
};
